import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import CompanyLayout from '../components/CompanyLayout';
import { jobApiService, Job } from '../../recruiter/services/jobApi';
import { recruiterApplicationApiService, Application, UpdateStageRequest } from '../services/applicationApi';
import CandidateProfileModal from '../components/CandidateProfileModal';

// Stage transition rules from job-service
// Simplified flow: Applied → Interview → Offer → Rejected
const VALID_TRANSITIONS: Record<string, string[]> = {
  applied: ['interview', 'rejected'],
  interview: ['offered', 'rejected'],
  offered: [],
  rejected: [],
};

// Messaging allowed stages (from messaging-service)
const MESSAGING_ALLOWED_STAGES = ['interview', 'offered'];

type SortOption = 'date_asc' | 'date_desc' | 'name_asc' | 'name_desc' | 'stage';

/**
 * Applications Review Page
 * Browse candidates who applied for your jobs
 * View candidate profiles, update stages, and message candidates
 */
const ApplicationsReview: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // Jobs state
  const [jobs, setJobs] = useState<Job[]>([]);
  const [selectedJobId, setSelectedJobId] = useState<string | null>(null);
  const [loadingJobs, setLoadingJobs] = useState(true);

  // Applications state
  const [applications, setApplications] = useState<Application[]>([]);
  const [loadingApplications, setLoadingApplications] = useState(false);

  // Sorting
  const [sortOption, setSortOption] = useState<SortOption>('date_asc');

  // Modal state
  const [selectedApplication, setSelectedApplication] = useState<Application | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Stage update state
  const [updatingStage, setUpdatingStage] = useState(false);
  const [stageError, setStageError] = useState<string | null>(null);

  // Fetch jobs on mount
  useEffect(() => {
    fetchJobs();
  }, []);

  // Fetch applications when job is selected
  useEffect(() => {
    if (selectedJobId) {
      fetchApplicationsForJob(selectedJobId);
    } else {
      setApplications([]);
    }
  }, [selectedJobId]);

  const fetchJobs = async () => {
    try {
      setLoadingJobs(true);

      // Get current user ID from localStorage
      const userDataString = localStorage.getItem('authUser');
      if (!userDataString) {
        console.warn('No auth user found');
        setJobs([]);
        return;
      }

      const userData = JSON.parse(userDataString);
      const userId = userData.id || userData._id || userData.user_id;

      if (!userId) {
        console.warn('No user ID found');
        setJobs([]);
        return;
      }

      // Fetch all jobs and filter by current user
      const { jobs: allJobs } = await jobApiService.getAllJobs({ limit: 100 });
      const userJobs = allJobs.filter((job) => {
        const postedBy = job.postedBy || job.recruiterId || job.companyId;
        return postedBy === userId;
      });

      console.log(`Found ${userJobs.length} jobs for current user`);
      setJobs(userJobs);

      // Auto-select job from URL query param
      const jobIdParam = searchParams.get('jobId');
      if (jobIdParam && userJobs.some(j => (j._id || j.id) === jobIdParam)) {
        setSelectedJobId(jobIdParam);
      }
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
    } finally {
      setLoadingJobs(false);
    }
  };

  const fetchApplicationsForJob = async (jobId: string) => {
    try {
      setLoadingApplications(true);
      const jobApplications = await recruiterApplicationApiService.getJobApplications(jobId);
      setApplications(jobApplications);
    } catch (error) {
      console.error(`Failed to fetch applications for job ${jobId}:`, error);
      setApplications([]);
    } finally {
      setLoadingApplications(false);
    }
  };

  // Sort applications
  const sortedApplications = [...applications].sort((a, b) => {
    switch (sortOption) {
      case 'date_asc': // First come first serve (oldest first)
        return new Date(a.appliedAt).getTime() - new Date(b.appliedAt).getTime();
      case 'date_desc': // Newest first
        return new Date(b.appliedAt).getTime() - new Date(a.appliedAt).getTime();
      case 'name_asc':
        return (a.candidateName || '').localeCompare(b.candidateName || '');
      case 'name_desc':
        return (b.candidateName || '').localeCompare(a.candidateName || '');
      case 'stage':
        const stageOrder = ['applied', 'interview', 'offered', 'rejected'];
        return stageOrder.indexOf(a.currentStage) - stageOrder.indexOf(b.currentStage);
      default:
        return 0;
    }
  });

  const handleCandidateClick = (application: Application) => {
    setSelectedApplication(application);
    setStageError(null);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedApplication(null);
    setStageError(null);
  };

  const canTransitionTo = (currentStage: string, newStage: string): boolean => {
    const allowed = VALID_TRANSITIONS[currentStage] || [];
    return allowed.includes(newStage);
  };

  const getTransitionErrorMessage = (currentStage: string, newStage: string): string => {
    if (currentStage === 'offered') {
      return 'This application has reached the final "Offered" stage and cannot be changed.';
    }
    if (currentStage === 'rejected') {
      return 'Rejected applications cannot be updated. Please create a new application if needed.';
    }

    const allowed = VALID_TRANSITIONS[currentStage] || [];
    if (allowed.length === 0) {
      return `No stage transitions are available from "${currentStage}".`;
    }

    return `Cannot transition from "${currentStage}" to "${newStage}". Valid options are: ${allowed.join(', ')}.`;
  };

  const handleStageChange = async (newStage: Application['currentStage']) => {
    if (!selectedApplication) return;

    // Validate transition
    if (!canTransitionTo(selectedApplication.currentStage, newStage)) {
      setStageError(getTransitionErrorMessage(selectedApplication.currentStage, newStage));
      return;
    }

    try {
      setUpdatingStage(true);
      setStageError(null);

      const request: UpdateStageRequest = {
        stage: newStage,
        notes: `Stage updated to ${newStage}`,
      };

      const updatedApplication = await recruiterApplicationApiService.updateApplicationStage(
        selectedApplication.id,
        request
      );

      // Update local state
      setApplications((prev) =>
        prev.map((app) => (app.id === selectedApplication.id ? updatedApplication : app))
      );
      setSelectedApplication(updatedApplication);

    } catch (error: any) {
      console.error('Error updating stage:', error);
      setStageError(error.message || 'Failed to update application stage. Please try again.');
    } finally {
      setUpdatingStage(false);
    }
  };

  const handleMessageCandidate = () => {
    if (!selectedApplication) return;

    // Check if messaging is allowed for current stage
    if (!MESSAGING_ALLOWED_STAGES.includes(selectedApplication.currentStage)) {
      setStageError(
        `Messaging is only available when the application is at: ${MESSAGING_ALLOWED_STAGES.join(', ')}. Current stage: ${selectedApplication.currentStage}`
      );
      return;
    }

    // Navigate to messages with application context
    navigate(`/companies/messages?application=${selectedApplication.id}&candidate=${selectedApplication.candidateId}`);
  };

  const getStageColor = (stage: string) => {
    switch (stage) {
      case 'applied':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'interview':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'offered':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const selectedJob = jobs.find((j) => (j.id || j._id) === selectedJobId);

  return (
    <CompanyLayout>
      <div className="space-y-6">
        {/* Header Card */}
        <div className="bg-white rounded-2xl p-6">
          <h2 className="text-2xl font-semibold text-[#0B111E] mb-2">Review Applications</h2>
          <p className="text-sm text-[#757575]">
            Browse and review candidates who have applied for your job postings
          </p>
        </div>

        {/* Job Selection */}
        <div className="bg-white rounded-2xl p-6">
          <h3 className="text-lg font-semibold text-[#0B111E] mb-4">Select a Job to View Applications</h3>
          {loadingJobs ? (
            <div className="flex items-center gap-2">
              <div className="w-5 h-5 border-2 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
              <p className="text-sm text-[#757575]">Loading your jobs...</p>
            </div>
          ) : jobs.length === 0 ? (
            <div className="text-center py-8">
              <svg className="w-12 h-12 mx-auto mb-3 text-[#A5A5A5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V4a2 2 0 00-2-2h-4a2 2 0 00-2 2v2m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              <p className="text-[#757575]">No jobs found. Create a job posting first.</p>
              <button
                onClick={() => navigate('/companies/job-posting')}
                className="mt-3 px-4 py-2 bg-[#015FA6] text-white rounded-lg text-sm font-medium hover:bg-[#014580] transition-colors"
              >
                Post a Job
              </button>
            </div>
          ) : (
            <select
              value={selectedJobId || ''}
              onChange={(e) => setSelectedJobId(e.target.value || null)}
              className="w-full px-4 py-3 rounded-lg bg-[#F1F3F4] text-[#0B111E] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6] text-sm"
            >
              <option value="">Select a job to view applications...</option>
              {jobs.map((job) => {
                const jobId = job.id || job._id;
                return (
                  <option key={jobId} value={jobId}>
                    {job.title} {job.location && `- ${typeof job.location === 'string' ? job.location : job.location.city}`}
                  </option>
                );
              })}
            </select>
          )}
        </div>

        {/* Applications List */}
        {selectedJobId && (
          <div className="bg-white rounded-2xl p-6">
            {/* Header with job info and sorting */}
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6 pb-4 border-b border-[#EDEDED]">
              <div>
                <h3 className="text-lg font-semibold text-[#0B111E]">
                  {selectedJob?.title || 'Job Applications'}
                </h3>
                <p className="text-sm text-[#757575]">
                  {applications.length} application{applications.length !== 1 ? 's' : ''} received
                </p>
              </div>

              {/* Sorting */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-[#757575]">Sort by:</label>
                <select
                  value={sortOption}
                  onChange={(e) => setSortOption(e.target.value as SortOption)}
                  className="px-3 py-2 rounded-lg bg-[#F1F3F4] text-[#0B111E] border border-[#EDEDED] focus:outline-none focus:border-[#015FA6] text-sm"
                >
                  <option value="date_asc">Date Applied (Oldest First)</option>
                  <option value="date_desc">Date Applied (Newest First)</option>
                  <option value="name_asc">Name (A-Z)</option>
                  <option value="name_desc">Name (Z-A)</option>
                  <option value="stage">Application Stage</option>
                </select>
              </div>
            </div>

            {/* Loading state */}
            {loadingApplications ? (
              <div className="flex items-center justify-center py-12">
                <div className="flex flex-col items-center gap-2">
                  <div className="w-8 h-8 border-4 border-[#015FA6] border-t-transparent rounded-full animate-spin" />
                  <p className="font-medium text-sm text-[#757575]">Loading applications...</p>
                </div>
              </div>
            ) : sortedApplications.length === 0 ? (
              <div className="text-center py-12">
                <svg className="w-16 h-16 mx-auto mb-4 text-[#A5A5A5]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p className="text-lg font-medium text-[#0B111E]">No applications yet</p>
                <p className="text-sm text-[#757575] mt-1">Candidates haven't applied to this job yet</p>
              </div>
            ) : (
              /* Applications Table */
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-[#F9F9F9] border-b border-[#EDEDED]">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">#</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">Candidate</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">Applied Date</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">Stage</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-[#515151] uppercase tracking-wider">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-[#EDEDED]">
                    {sortedApplications.map((application, index) => (
                      <tr
                        key={application.id}
                        className="hover:bg-[#F9F9F9] transition-colors cursor-pointer"
                        onClick={() => handleCandidateClick(application)}
                      >
                        <td className="px-4 py-4 text-sm text-[#757575]">{index + 1}</td>
                        <td className="px-4 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#015FA6] to-[#0288D1] flex items-center justify-center text-white font-semibold text-sm">
                              {(application.candidateName || 'C').charAt(0).toUpperCase()}
                            </div>
                            <span className="font-medium text-[#0B111E]">
                              {application.candidateName || 'Candidate'}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-4 text-sm text-[#757575]">
                          {application.candidateEmail || '-'}
                        </td>
                        <td className="px-4 py-4 text-sm text-[#757575]">
                          {formatDate(application.appliedAt)}
                        </td>
                        <td className="px-4 py-4">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold border capitalize ${getStageColor(application.currentStage)}`}>
                            {application.currentStage}
                          </span>
                        </td>
                        <td className="px-4 py-4">
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleCandidateClick(application);
                            }}
                            className="px-3 py-1.5 bg-[#015FA6] text-white rounded-lg text-xs font-medium hover:bg-[#014580] transition-colors"
                          >
                            View Profile
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Candidate Profile Modal */}
      <CandidateProfileModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        application={selectedApplication}
        onStageChange={handleStageChange}
        onMessage={handleMessageCandidate}
        updatingStage={updatingStage}
        stageError={stageError}
        onClearError={() => setStageError(null)}
        validTransitions={VALID_TRANSITIONS}
        messagingAllowedStages={MESSAGING_ALLOWED_STAGES}
      />
    </CompanyLayout>
  );
};

export default ApplicationsReview;
